import type { ReactNode } from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { cn } from '@/lib/cn';
import { ABAS, RAIZ_DA_ADMINISTRACAO } from '@/features/admin/navegacao';

/**
 * Casca da administração: o título, a explicação e a sub-navegação.
 *
 * As cinco abas são **rotas**, não estado de componente. É o que faz o endereço
 * `/administracao/equipes` ser compartilhável, recarregável e alcançável pelo
 * botão "voltar" do navegador — três coisas que um `useState('equipes')`
 * quebraria de uma vez, e que numa tela de configuração importam: o pedido
 * "olha aqui, ó" com um link é metade do suporte interno.
 */
export function AdminLayout({ children }: { children?: ReactNode }) {
  const pathname = useRouterState({ select: (estado) => estado.location.pathname });

  const abaAtiva = ABAS.find((a) => {
    const destino = `${RAIZ_DA_ADMINISTRACAO}/${a.caminho}`;
    return pathname === destino || pathname.startsWith(`${destino}/`);
  });

  return (
    <div className="mx-auto max-w-[1400px] space-y-4">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <div>
          <h1 className="text-base font-semibold">Administração</h1>
          {abaAtiva ? (
            <p className="mt-0.5 text-xs text-muted-foreground">{abaAtiva.resumo}</p>
          ) : null}
        </div>
      </header>

      <nav aria-label="Catálogos" className="border-b border-border">
        <ul className="-mb-px flex flex-wrap gap-1">
          {ABAS.map((aba) => {
            const destino = `${RAIZ_DA_ADMINISTRACAO}/${aba.caminho}`;
            const ativa = pathname === destino || pathname.startsWith(`${destino}/`);
            const Icone = aba.icone;
            return (
              <li key={aba.caminho}>
                <Link
                  to={destino}
                  aria-current={ativa ? 'page' : undefined}
                  className={cn(
                    'inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium',
                    'transition-colors focus-visible:shadow-glow focus-visible:outline-none',
                    ativa
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
                  )}
                >
                  <Icone className="h-4 w-4" aria-hidden />
                  {aba.rotulo}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {children ?? <Outlet />}
    </div>
  );
}
